'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { IconArrowRight, IconSparkles } from '@tabler/icons-react';
import { StrapiImage } from '../ui/strapi-image';
import { jakarta } from '@/lib/fonts';
import { cn } from '@/lib/utils';

export interface ContentSectionProps {
  heading: string;
  sub_heading?: string;
  tag?: string;
  content?: string;
  image?: any;
  image_position?: 'left' | 'right' | 'center';
  cta_text?: string;
  cta_link?: string;
  locale?: string;
  id?: number | string;
  index?: number;
  page_title?: string;
  page_slug?: string;
}

export const ContentSection: React.FC<ContentSectionProps> = ({
  heading,
  sub_heading,
  tag,
  content,
  image,
  image_position = 'right',
  cta_text,
  cta_link,
  locale = 'en',
  index = 0,
  page_title,
  page_slug,
}) => {
  const resolvedPageTitle = page_title || (locale === 'fr' ? 'À propos' : 'About');
  const resolvedPageSlug = page_slug || 'about';
  const imageUrl = image?.data?.attributes?.url || image?.url || image;
  const isImageLeft = image_position === 'left';
  const isImageCenter = image_position === 'center';
  const isFirst = index === 0;
  const isEven = index % 2 === 0;

  // Split content by double newlines or single newlines into clean paragraphs
  const paragraphs = content
    ? content
        .split('\n\n')
        .flatMap((p) => p.split('\n'))
        .filter((p) => p.trim().length > 0)
    : [];

  const targetLink = cta_link
    ? cta_link.startsWith('http') || cta_link.startsWith('/')
      ? cta_link.startsWith('/')
        ? `/${locale}${cta_link}`
        : cta_link
      : `/${locale}/${cta_link}`
    : `/${locale}/contact`;

  return (
    <section
      className={cn(
        'relative overflow-hidden transition-colors duration-300 border-b border-[#e5e2f5] dark:border-neutral-800/80',
        isEven
          ? 'bg-[#f4f2fc] dark:bg-neutral-900/90'
          : 'bg-white dark:bg-neutral-950',
        isFirst ? 'pt-28 md:pt-36 pb-16 md:pb-24' : 'py-16 md:py-24'
      )}
    >
      {/* Discreet background ambient lighting */}
      <div className="absolute inset-0 pointer-events-none -z-10 overflow-hidden">
        <div
          className={cn(
            'absolute top-1/2 -translate-y-1/2 w-[550px] h-[550px] rounded-full blur-[150px] opacity-[0.04]',
            isImageLeft ? 'left-0 bg-[#c02029]' : 'right-0 bg-[#c02029]'
          )}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {isImageCenter ? (
          /* Center Layout */
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto">
            {/* Streamlined Breadcrumb Header */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="flex items-center flex-wrap justify-center gap-2 text-xs sm:text-sm font-medium text-[#50456c] dark:text-neutral-400 mb-6 tracking-wide"
            >
              <Link
                href={`/${locale}`}
                className="hover:text-[#c02029] transition-colors"
              >
                {locale === 'fr' ? 'Accueil' : 'Home'}
              </Link>
              <span className="text-[#c02029] font-light">—</span>
              {isFirst ? (
                <span className="text-[#2e1452] dark:text-neutral-200 font-semibold">
                  {resolvedPageTitle}
                </span>
              ) : (
                <>
                  <Link
                    href={`/${locale}/${resolvedPageSlug}`}
                    className="hover:text-[#c02029] transition-colors"
                  >
                    {resolvedPageTitle}
                  </Link>
                  {tag && (
                    <>
                      <span className="text-neutral-400 font-light">—</span>
                      <span className="text-[#2e1452] dark:text-neutral-200 font-semibold">
                        {tag}
                      </span>
                    </>
                  )}
                </>
              )}
              {isFirst && tag && (
                <>
                  <span className="text-neutral-400 font-light">—</span>
                  <span className="text-[#c02029] font-medium">{tag}</span>
                </>
              )}
            </motion.div>

            {/* Main Section Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className={cn(
                jakarta.className,
                'text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-[#2e1452] dark:text-white mb-5 leading-[1.15]'
              )}
            >
              {heading}
            </motion.h2>

            {/* Subheading */}
            {sub_heading && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="text-base sm:text-lg md:text-xl font-medium text-[#3b2d56] dark:text-neutral-200 mb-6 max-w-3xl leading-relaxed"
              >
                {sub_heading}
              </motion.p>
            )}

            {/* Paragraphs */}
            {paragraphs.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="space-y-4 text-[#5e5873] dark:text-neutral-300 text-base sm:text-lg leading-relaxed mb-8 max-w-3xl text-center"
              >
                {paragraphs.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </motion.div>
            )}

            {/* Illustration Card */}
            {imageUrl && (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="w-full relative rounded-3xl p-2 sm:p-3 bg-white dark:bg-neutral-900 shadow-xl shadow-[#301754]/5 dark:shadow-none border border-[#e5e2f5] dark:border-neutral-800 mb-8 group"
              >
                <div className="relative aspect-[16/9] w-full rounded-2xl overflow-hidden bg-neutral-100 dark:bg-neutral-950">
                  <StrapiImage
                    src={imageUrl}
                    alt={heading}
                    fill
                    className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
              </motion.div>
            )}

            {/* Signature Pill Button with Discreet Red */}
            {cta_text && (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Link
                  href={targetLink}
                  className="inline-flex items-center gap-4 pl-6 sm:pl-7 pr-2 py-2 rounded-full bg-white dark:bg-neutral-900 border border-neutral-200/90 dark:border-neutral-700 shadow-md shadow-neutral-200/60 dark:shadow-none hover:shadow-xl hover:shadow-[#c02029]/15 transition-all duration-300 group hover:border-[#c02029]/40"
                >
                  <span className="text-base font-semibold text-[#2e1452] dark:text-white group-hover:text-[#c02029] transition-colors">
                    {cta_text}
                  </span>
                  <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#c02029] text-white flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:bg-[#a61b23] shadow-md shadow-[#c02029]/25 flex-shrink-0">
                    <IconArrowRight className="w-5 h-5 stroke-[2.5] transition-transform duration-300 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </motion.div>
            )}
          </div>
        ) : (
          /* Split Grid Layout (Left / Right) */
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Content Column */}
            <motion.div
              initial={{ opacity: 0, x: isImageLeft ? 25 : -25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className={cn(
                'lg:col-span-6 flex flex-col justify-center',
                isImageLeft ? 'lg:order-2' : 'lg:order-1'
              )}
            >
              {/* Streamlined Breadcrumb Header */}
              <div className="flex items-center flex-wrap gap-2 text-xs sm:text-sm font-medium text-[#50456c] dark:text-neutral-400 mb-5 tracking-wide">
                <Link
                  href={`/${locale}`}
                  className="hover:text-[#c02029] transition-colors"
                >
                  {locale === 'fr' ? 'Accueil' : 'Home'}
                </Link>
                <span className="text-[#c02029] font-light">—</span>
                {isFirst ? (
                  <span className="text-[#2e1452] dark:text-neutral-200 font-semibold">
                    {resolvedPageTitle}
                  </span>
                ) : (
                  <>
                    <Link
                      href={`/${locale}/${resolvedPageSlug}`}
                      className="hover:text-[#c02029] transition-colors"
                    >
                      {resolvedPageTitle}
                    </Link>
                    {tag && (
                      <>
                        <span className="text-neutral-400 font-light">—</span>
                        <span className="text-[#2e1452] dark:text-neutral-200 font-semibold">
                          {tag}
                        </span>
                      </>
                    )}
                  </>
                )}
                {isFirst && tag && (
                  <>
                    <span className="text-neutral-400 font-light">—</span>
                    <span className="text-[#c02029] font-medium">{tag}</span>
                  </>
                )}
              </div>

              {/* Main Section Title */}
              <h2
                className={cn(
                  jakarta.className,
                  'text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-[#2e1452] dark:text-white mb-5 leading-[1.12]'
                )}
              >
                {heading}
              </h2>

              {/* Subheading */}
              {sub_heading && (
                <p className="text-base sm:text-lg font-medium text-[#3b2d56] dark:text-neutral-200 leading-relaxed mb-5">
                  {sub_heading}
                </p>
              )}

              {/* Paragraphs */}
              {paragraphs.length > 0 && (
                <div className="space-y-4 text-[#5e5873] dark:text-neutral-300 text-base sm:text-lg leading-relaxed mb-8">
                  {paragraphs.map((p, idx) => (
                    <p key={idx}>{p}</p>
                  ))}
                </div>
              )}

              {/* Signature Pill Button with Discreet Red */}
              {cta_text && (
                <div className="pt-1">
                  <Link
                    href={targetLink}
                    className="inline-flex items-center gap-4 pl-6 sm:pl-7 pr-2 py-2 rounded-full bg-white dark:bg-neutral-900 border border-neutral-200/90 dark:border-neutral-700 shadow-md shadow-neutral-200/60 dark:shadow-none hover:shadow-xl hover:shadow-[#c02029]/15 transition-all duration-300 group hover:border-[#c02029]/40"
                  >
                    <span className="text-base font-semibold text-[#2e1452] dark:text-white group-hover:text-[#c02029] transition-colors">
                      {cta_text}
                    </span>
                    <span className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#c02029] text-white flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-hover:bg-[#a61b23] shadow-md shadow-[#c02029]/25 flex-shrink-0">
                      <IconArrowRight className="w-5 h-5 stroke-[2.5] transition-transform duration-300 group-hover:translate-x-0.5" />
                    </span>
                  </Link>
                </div>
              )}
            </motion.div>

            {/* Illustration Image Column */}
            <motion.div
              initial={{ opacity: 0, x: isImageLeft ? -25 : 25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className={cn(
                'lg:col-span-6',
                isImageLeft ? 'lg:order-1' : 'lg:order-2'
              )}
            >
              <div className="relative group p-2 sm:p-3 rounded-3xl bg-white dark:bg-neutral-900 shadow-xl shadow-[#301754]/5 dark:shadow-none border border-[#e5e2f5] dark:border-neutral-800 hover:border-[#c02029]/30 transition-all duration-500">
                {/* Subtle discreet red ambient glow behind image card */}
                <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-[#c02029]/15 via-rose-500/10 to-transparent blur-xl opacity-30 group-hover:opacity-60 transition duration-500 pointer-events-none" />

                <div className="relative aspect-[16/10] sm:aspect-[16/9] w-full rounded-2xl overflow-hidden bg-neutral-100 dark:bg-neutral-950">
                  {imageUrl ? (
                    <StrapiImage
                      src={imageUrl}
                      alt={heading}
                      fill
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-neutral-100 dark:bg-neutral-900 text-neutral-400">
                      <IconSparkles className="w-12 h-12 text-[#c02029]/40 animate-pulse" />
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ContentSection;
